import { applyFilters } from './statistics'
import type { StatisticFilters, StatisticsMetadata, StatisticsPayload } from '../types'

let fallbackPayload: Promise<StatisticsPayload> | null = null

async function loadFallback() {
  fallbackPayload ??= fetch('/data/keyword-statistics.json').then(async (response) => {
    if (!response.ok) throw new Error('통계 데이터 파일을 읽지 못했습니다.')
    return response.json() as Promise<StatisticsPayload>
  })
  return fallbackPayload
}

function shouldUseFallback(response: Response) {
  return response.status === 404 || response.status === 500 || response.status === 503
}

export async function loadStatisticsMetadata(): Promise<StatisticsMetadata> {
  try {
    const response = await fetch('/api/keyword-statistics', {
      headers: { Accept: 'application/json' },
    })
    if (response.ok) return response.json() as Promise<StatisticsMetadata>
    if (!shouldUseFallback(response)) throw new Error('통계 데이터베이스 정보를 읽지 못했습니다.')
  } catch (error) {
    if (!(error instanceof TypeError)) throw error
  }

  const payload = await loadFallback()
  const months = [...new Set(payload.facts.map((record) => record.month))].sort()
  const keywords = [...new Set(payload.facts.map((record) => record.keyword))]
    .sort((left, right) => left.localeCompare(right, 'ko'))
    .map((name) => ({ name, lastCollectedMonth: months.at(-1) ?? null }))
  return {
    generatedAt: payload.generatedAt,
    source: payload.source,
    minMonth: months[0] ?? '',
    maxMonth: months.at(-1) ?? '',
    keywords,
  }
}

export async function loadStatistics(filters: StatisticFilters): Promise<StatisticsPayload> {
  try {
    const response = await fetch('/api/keyword-statistics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(filters),
    })
    if (response.ok) return response.json() as Promise<StatisticsPayload>
    if (!shouldUseFallback(response)) {
      const body = await response.json().catch(() => null) as { error?: string } | null
      throw new Error(body?.error ?? '통계 데이터를 조회하지 못했습니다.')
    }
  } catch (error) {
    if (!(error instanceof TypeError)) throw error
  }

  const payload = await loadFallback()
  return { ...payload, facts: applyFilters(payload.facts, filters) }
}
